import { ColumnDef } from "@tanstack/react-table";
import React, { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { WarehousePermitWorker } from "../../../constants/common";
import { Button } from "../../button";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";

interface AccompanyingPersonnelGroupTableProps {
  addedWorkers: WarehousePermitWorker[];
  setAddedWorkers: Dispatch<SetStateAction<WarehousePermitWorker[]>>;
  setEditingWorker: React.Dispatch<
    React.SetStateAction<WarehousePermitWorker | null>
  >;
  setIsAddVisitorFormVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onFormReset?: () => void;

  isFieldsDisabled: boolean;
}

const AccompanyingPersonnelGroupTable = ({
  addedWorkers,
  setAddedWorkers,
  setEditingWorker,
  setIsAddVisitorFormVisible,
  onFormReset,
  isFieldsDisabled,
}: AccompanyingPersonnelGroupTableProps) => {
  const { t } = useTranslation();

  const handleEdit = useCallback(
    (worker: WarehousePermitWorker) => {
      setEditingWorker(worker);
      setIsAddVisitorFormVisible(true);
    },
    [setEditingWorker, setIsAddVisitorFormVisible]
  );

  const handleDelete = useCallback(
    (id: string) => {
      setAddedWorkers((prev) => prev.filter((visitor) => visitor.id !== id));
      setEditingWorker(null);

      // Reset the form when a visitor is deleted
      if (onFormReset) {
        onFormReset();
      }
    },
    [onFormReset, setAddedWorkers, setEditingWorker]
  );

  const columns: ColumnDef<WarehousePermitWorker>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("name"),
      },
      {
        accessorKey: "nationality",
        header: t("nationality"),
      },

      {
        accessorKey: "residencePermitNo",
        header: t("residencePermitNo"),
      },

      {
        accessorKey: "issuingAuthority",
        header: t("issuingAuthority"),
      },

      {
        accessorKey: "issueDate",
        header: t("issueDate"),
        cell: ({ row }) => {
          return (
            <span>{new Date(row.original.issueDate).toLocaleDateString()}</span>
          );
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <div className="flex gap-1 items-center">
              <Button
                type="button"
                onClick={() => handleEdit(row.original)}
                className="py-0.5"
              >
                {isFieldsDisabled ? t("view") : t("edit")}
              </Button>
              {!isFieldsDisabled && (
                <Button
                  onClick={() => handleDelete(row.original.id)}
                  className="bg-red-600 hover:bg-red-500 py-0.5"
                >
                  {t("delete")}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [t, isFieldsDisabled, handleEdit, handleDelete]
  );

  return (
    <TableWrapper
      data={addedWorkers}
      columns={columns}
      isPagination={false}
      isColumnChooser={false}
    />
  );
};

export default React.memo(AccompanyingPersonnelGroupTable);
